import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { FiMessageCircle } from 'react-icons/fi';
import { gameAPI } from '../../services/api';
import { useTelegram } from '../../hooks/useTelegram';

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  padding: 16px;
  padding-bottom: 90px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 4px;
`;

const Sub = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 13px;
  margin-bottom: 28px;
`;

const Section = styled.div`
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const SectionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${p => p.$bg || 'rgba(0,111,255,0.15)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
`;

/* Card for multi-region donate items */
const Card = styled.a`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: block;
  margin-bottom: 8px;
  &:active { transform: scale(0.98); }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
`;

const OrderBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #006FFF;
  flex-shrink: 0;
`;

const PriceRow = styled.div`
  display: flex;
  gap: 6px;
`;

const PriceChip = styled.div`
  flex: 1;
  background: ${p => p.$bg || 'rgba(255,255,255,0.04)'};
  border-radius: 10px;
  padding: 8px 10px;
  text-align: center;
`;

const FlagIcon = styled.img`
  width: 20px;
  height: 14px;
  border-radius: 2px;
  object-fit: contain;
  margin-bottom: 4px;
`;

const ChipPrice = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.$best ? '#4CD964' : '#fff'};
`;

/* Flat card for wallet / xbox items (single price) */
const FlatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const FlatCard = styled.a`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: block;
  &:active { transform: scale(0.97); }
`;

const FlatCardImg = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: #1a1a2e;
  position: relative;
  overflow: hidden;
`;

const FlatCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const FlatCardBody = styled.div`
  padding: 12px 14px;
`;

const FlatCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const FlatCardPrice = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #fff;
`;

const OldPrice = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: rgba(255,255,255,0.3);
  text-decoration: line-through;
  margin-left: 6px;
`;

const SECTION_CONFIG = {
  wallet: { title: '💳 Пополнение кошелька PSN', icon: '💳', iconBg: 'rgba(0,111,255,0.15)' },
  fc26: { title: '⚽ EA FC 26 Points', icon: '⚽', iconBg: 'rgba(0,185,0,0.15)' },
  arc_raiders: { title: '🚀 ARC Raiders', icon: '🚀', iconBg: 'rgba(0,200,200,0.15)' },
  fortnite: { title: '🔫 Fortnite V-Bucks', icon: '🔫', iconBg: 'rgba(147,112,219,0.15)' },
  helldivers: { title: '🪖 Helldivers 2', icon: '🪖', iconBg: 'rgba(100,150,0,0.15)' },
  cod: { title: '🎯 CoD: BO7 & Warzone', icon: '🎯', iconBg: 'rgba(255,100,0,0.15)' },
  warface: { title: '💥 Warface', icon: '💥', iconBg: 'rgba(255,50,50,0.15)' },
  apex: { title: '🏆 Apex Legends', icon: '🏆', iconBg: 'rgba(200,0,0,0.15)' },
  diablo: { title: '😈 Diablo IV', icon: '😈', iconBg: 'rgba(150,0,0,0.15)' },
  wot: { title: '🎖️ World of Tanks', icon: '🎖️', iconBg: 'rgba(255,150,0,0.15)' },
  overwatch: { title: '🦸 Overwatch 2', icon: '🦸', iconBg: 'rgba(255,150,0,0.15)' },
  pubg: { title: '🪂 PUBG', icon: '🪂', iconBg: 'rgba(255,200,0,0.15)' },
  rust: { title: '🔧 Rust', icon: '🔧', iconBg: 'rgba(180,100,50,0.15)' },
  warthunder: { title: '✈️ War Thunder', icon: '✈️', iconBg: 'rgba(0,100,0,0.15)' },
  // Xbox GamePass moved to /xbox/gamepass
};

// Section display order
const SECTION_ORDER = [
  'fc26', 'fortnite', 'cod', 'apex', 'helldivers', 'overwatch',
  'pubg', 'wot', 'warface', 'diablo', 'rust', 'warthunder', 'arc_raiders',
  'wallet',
];

const ORDER_URL = 'https://t.me/openps_support';

const FLAGS = {
  tr: '/icons/flag-tr.png?v=8',
  ua: '/icons/flag-ua.png?v=8',
  in: '/icons/flag-in.png?v=8',
};

const Donates = () => {
  const navigate = useNavigate();
  const { subtype: filterSubtype } = useParams();
  const { tg, hapticFeedback } = useTelegram();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const goBack = () => navigate('/');
    if (tg) { tg.setHeaderColor('#0a0a0f'); tg.BackButton.show(); tg.BackButton.onClick(goBack); }
    gameAPI.getDonates().then(setItems).catch(() => {});
    return () => { if (tg) { tg.BackButton.offClick(goBack); tg.BackButton.hide(); } };
  }, [tg, navigate]);

  // Group by subtype
  const groups = {};
  items.forEach(i => {
    const key = i.subtype || 'other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(i);
  });

  // Sort items within groups by price ascending
  Object.values(groups).forEach(list => list.sort((a, b) => a.price_rub - b.price_rub));

  const getImgUrl = (img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    return `https://open-ps.ru${img}`;
  };

  // Ordered section keys
  let orderedKeys = SECTION_ORDER.filter(k => groups[k]);
  // Add any remaining keys not in order
  Object.keys(groups).forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });
  // Filter by subtype if navigated from specific card
  if (filterSubtype && groups[filterSubtype]) {
    orderedKeys = [filterSubtype];
  }

  return (
    <Page>
      <Title>{filterSubtype && SECTION_CONFIG[filterSubtype] ? SECTION_CONFIG[filterSubtype].title : '💎 Донаты и валюта'}</Title>
      <Sub>{filterSubtype ? 'Выберите нужный номинал' : 'Внутриигровая валюта, подписки и пополнения'}</Sub>

      {orderedKeys.map(key => {
        const list = groups[key];
        const conf = SECTION_CONFIG[key] || { title: key, icon: '🎯', iconBg: 'rgba(0,111,255,0.15)' };
        const hasMultiRegion = list.some(i => i.prices);

        return (
          <Section key={key}>
            <SectionHeader>
              <SectionIcon $bg={conf.iconBg}>{conf.icon}</SectionIcon>
              <SectionTitle>{conf.title}</SectionTitle>
            </SectionHeader>

            {hasMultiRegion ? (
              // Multi-region cards — list with 3 price chips
              list.map(item => {
                const prices = item.prices || {};
                const trP = prices.tr?.price_rub;
                const uaP = prices.ua?.price_rub;
                const inP = prices.in?.price_rub;
                const minP = Math.min(...[trP, uaP, inP].filter(Boolean));

                return (
                  <Card key={item.id} href={ORDER_URL} target="_blank" onClick={() => hapticFeedback?.('light')}>
                    <CardTop>
                      <CardTitle>{item.title}</CardTitle>
                      <OrderBadge><FiMessageCircle size={11} /> Заказать</OrderBadge>
                    </CardTop>
                    <PriceRow>
                      {trP && (
                        <PriceChip $bg="rgba(255,0,0,0.06)">
                          <FlagIcon src={FLAGS.tr} alt="TR" />
                          <ChipPrice $best={trP === minP}>{trP.toLocaleString('ru')} ₽</ChipPrice>
                        </PriceChip>
                      )}
                      {uaP && (
                        <PriceChip $bg="rgba(0,87,183,0.06)">
                          <FlagIcon src={FLAGS.ua} alt="UA" />
                          <ChipPrice $best={uaP === minP}>{uaP.toLocaleString('ru')} ₽</ChipPrice>
                        </PriceChip>
                      )}
                      {inP && (
                        <PriceChip $bg="rgba(255,153,0,0.06)">
                          <FlagIcon src={FLAGS.in} alt="IN" />
                          <ChipPrice $best={inP === minP}>{inP.toLocaleString('ru')} ₽</ChipPrice>
                        </PriceChip>
                      )}
                    </PriceRow>
                  </Card>
                );
              })
            ) : (
              // Flat cards — grid with image (wallet, xbox, etc)
              <FlatGrid>
                {list.map(item => {
                  const imgUrl = getImgUrl(item.image);
                  return (
                    <FlatCard key={item.id} href={ORDER_URL} target="_blank" onClick={() => hapticFeedback?.('light')}>
                      {imgUrl && (
                        <FlatCardImg>
                          <FlatCardImage src={imgUrl} alt={item.title} loading="lazy" />
                        </FlatCardImg>
                      )}
                      <FlatCardBody>
                        <FlatCardTitle>{item.title}</FlatCardTitle>
                        <FlatCardPrice>
                          {item.price_rub.toLocaleString('ru')} ₽
                          {item.old_price_rub && <OldPrice>{item.old_price_rub.toLocaleString('ru')} ₽</OldPrice>}
                        </FlatCardPrice>
                      </FlatCardBody>
                    </FlatCard>
                  );
                })}
              </FlatGrid>
            )}
          </Section>
        );
      })}
    </Page>
  );
};

export default Donates;
